import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import React from 'react';
import { Redirect } from 'react-router-dom';
import { isAfter } from 'date-fns';
import {
  ProductDetailPageQuery,
  ProductDetailPageQueryVariables
} from '../../generated/types/ProductDetailPageQuery';
import { SECONDS_TO_MILLISECONDS } from '../../constants';
import { REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS } from '../../constants';
import { VERTICALS, Vertical } from '../../constants';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { hrefFor } from '../../routers';
import { Loading } from '../../components/Loading';
import { useCountry } from '../../components/CountrySelector';
import { NotFoundPage } from '../NotFoundPage';
import { ProductDetailPage, PRODUCT_DETAIL_PAGE_FRAGMENT } from './ProductDetailPage';

const PRODUCT_DETAIL_PAGE_QUERY: TypedDocumentNode<
  ProductDetailPageQuery,
  ProductDetailPageQueryVariables
> = gql`
  query ProductDetailPageQuery($variantId: String!, $countryCode: String) {
    variant(id: $variantId) {
      activeEndI: active_end_i
      gender
      designerSlug: designer_slug
      productSlug: slug
      ...ProductDetailPageFragment
    }
  }
  ${PRODUCT_DETAIL_PAGE_FRAGMENT}
`;

interface Props {
  match: {
    params: { vertical: string; designerSlug: string; productSlug: string; variantId: string };
  };
}

export const ProductDetailPageContainer: React.FC<Props> = ({ match: { params } }) => {
  const { variantId } = params;
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();

  const { data, loading, error } = useQuery(PRODUCT_DETAIL_PAGE_QUERY, {
    variables: { variantId, countryCode }
  });

  if (loading || !data) return <Loading />;

  if (error) throwQueryError(error);

  if (!data.variant) return <NotFoundPage />;

  const { variant } = data;

  const vertical = VERTICALS[variant.gender as Vertical].toLowerCase();
  const productSlug = variant.productSlug;
  const designerSlug = variant.designerSlug as string;

  // if the product has expired a long time ago redirect to the designer page
  if (
    variant.activeEndI &&
    isAfter(
      new Date(),
      variant.activeEndI * SECONDS_TO_MILLISECONDS + REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS
    )
  )
    return <Redirect to={hrefFor.DesignerPage({ vertical, designerSlug })} />;

  // if the url is constructed incorrectly, redirect to the correct url
  if (
    params.vertical !== vertical ||
    params.designerSlug !== designerSlug ||
    params.productSlug !== productSlug
  )
    return (
      <Redirect
        to={hrefFor.ProductDetailPage({ vertical, designerSlug, productSlug, variantId })}
      />
    );

  return <ProductDetailPage variant={variant} />;
};
